import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  BookOpen,
  Home,
  Settings,
  Users,
  ClipboardList,
  CreditCard,
  Shield,
  LogOut,
  Shapes,
  Activity,
  Layers,
  ClipboardCheck,
  Flag,
  FileText,
  FlaskConical,
  ToggleLeft,
  Bug,
  Gauge,
  Heart,
  LifeBuoy,
} from "lucide-react";

import { ScioMark } from "@/components/ui/scio-mark";
import { BetaBadge } from "@/components/ui/beta-badge";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from "@/components/ui/sidebar";
import { useMutation, useQuery } from "@apollo/client/react";
import { LOGOUT } from "@/graphql/mutations/admin";
import { GET_FLAG_COUNTS } from "@/graphql/queries/admin";
import { GET_SUPPORT_REQUESTS } from "@/graphql/queries/support";
import { SupportDialog } from "@/components/support/support-dialog";
import { LegalFooter } from "@/components/legal/legal-footer";
import { useAuth } from "@/lib/auth/AuthContext";
import { FEATURE_DECISION_LAB, useFeature } from "@/lib/features";

export function AppSidebar() {
  const location = useLocation();
  const { isAdmin, devTools } = useAuth();
  // Hiding the item is a courtesy — /lab checks the same flag server-side.
  const hasDecisionLab = useFeature(FEATURE_DECISION_LAB);
  const [logout] = useMutation(LOGOUT);

  // Badge for the review queue: what children reported, what the AI
  // flagged, and the walls and theory gaps the app counted.
  const { data: flagData } = useQuery(GET_FLAG_COUNTS, { skip: !isAdmin });
  const flagCounts = (flagData as any)?.flagCounts || {};
  const flagTotal =
    (flagCounts.exercises || 0) + (flagCounts.theory || 0) + (flagCounts.ai_messages || 0);

  // Badge for the support queue. Derived from the open rows, so it takes itself
  // down when they are answered — a badge somebody has to remember to clear is
  // one that stops meaning anything.
  const { data: supportData } = useQuery(GET_SUPPORT_REQUESTS, {
    variables: { status: "open" },
    skip: !isAdmin,
  });
  const supportTotal = (supportData as any)?.openSupportRequests || 0;

  const [supportOpen, setSupportOpen] = useState(false);
  // The learner in view, when the URL names one. Everything else a request needs
  // — role, browser, the newest error — the server already knows; this is the one
  // thing only the screen knows, and „kuris vaikas" is the first question an
  // admin has about half of these.
  const learnerId = location.pathname.match(/^\/students\/([^/]+)/)?.[1];

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      // Full reload so the auth cache (me query) is cleared.
      window.location.assign("/auth/login");
    }
  };

  const mainNavItems = [
    {
      title: "Pradžia",
      url: "/",
      icon: Home,
    },
    {
      title: "Mokiniai",
      url: "/students",
      icon: Users,
    },
    {
      title: "Dalykai",
      url: "/subjects",
      icon: BookOpen,
    },
    // The household: who else looks after these children, and how a second
    // parent is let in. Sits beside „Mokiniai" because it answers the other half
    // of the same question — whose children these are.
    {
      title: "Šeima",
      url: "/family",
      icon: Heart,
    },
    {
      title: "Sesijos",
      url: "/sessions",
      icon: ClipboardList,
    },
    ...(hasDecisionLab
      ? [
          {
            title: "Sprendimų laboratorija",
            url: "/lab",
            icon: FlaskConical,
          },
        ]
      : []),
  ];

  const isActive = (url: string) => {
    if (url === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(url);
  };

  return (
    <Sidebar>
      <SidebarHeader className="border-b border-sidebar-border">
        <div className="flex items-center gap-2 px-2 py-2">
          <ScioMark size={24} className="text-primary" />
          <span className="font-semibold text-lg tracking-tight">Scio</span>
          {/* Beside the wordmark, so it names the app rather than the page.
              Absent from the learner's shell on purpose — see BetaBadge. */}
          <BetaBadge />
        </div>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Navigacija</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {mainNavItems.map((item) => (
                <SidebarMenuItem key={item.url}>
                  <SidebarMenuButton asChild isActive={isActive(item.url)}>
                    <Link to={item.url}>
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border">
        <SidebarMenu>
          {/* „Parašyk mums" — the grown-up's way in, and the only one they had
              none of. Above „Administravimas" and the logout because it is the
              thing somebody looks for when they are already stuck, and below
              everything else because it is not part of the day's work.

              A MODAL rather than a link, and that is the whole point: going to
              /pagalba to describe a problem attaches `path: "/pagalba"`, which
              is the one page that cannot be the problem. Opening in place keeps
              the screen they are complaining about — and keeps it on screen,
              where they can look at it while describing it. */}
          <SidebarMenuItem>
            <SidebarMenuButton onClick={() => setSupportOpen(true)}>
              <LifeBuoy className="h-4 w-4" />
              <span>Reikia pagalbos?</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          {isAdmin && (
          <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={location.pathname === "/admin"}>
              <Link to="/admin">
                <Shield className="h-4 w-4" />
                <span>Administravimas</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          )}
          {isAdmin && isActive("/admin") && (
            <>
              {/* The content workbench, development only: in production these
                  screens read a container's throwaway copy of what is already in
                  git. `devTools` is the same predicate that gates their
                  resolvers, so a link here cannot offer a page that will not
                  open. */}
              {devTools && (
                <>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={isActive("/admin/exercises")} className="pl-8">
                      <Link to="/admin/exercises">
                        <BookOpen className="h-4 w-4" />
                        <span>Užduotys</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={isActive("/admin/theory")} className="pl-8">
                      <Link to="/admin/theory">
                        <FileText className="h-4 w-4" />
                        <span>Teorija</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={isActive("/admin/prompt-layers")} className="pl-8">
                      <Link to="/admin/prompt-layers">
                        <Layers className="h-4 w-4" />
                        <span>Prompt sluoksniai</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={isActive("/admin/audits")} className="pl-8">
                      <Link to="/admin/audits">
                        <ClipboardCheck className="h-4 w-4" />
                        <span>Auditas</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </>
              )}
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={isActive("/admin/flagged")} className="pl-8">
                  <Link to="/admin/flagged">
                    <Flag className="h-4 w-4" />
                    <span>Peržiūrai</span>
                    {flagTotal > 0 && (
                      <span className="ml-auto rounded-full bg-orange-500 px-1.5 text-xs font-medium text-white">
                        {flagTotal}
                      </span>
                    )}
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={isActive("/admin/support")} className="pl-8">
                  <Link to="/admin/support">
                    <LifeBuoy className="h-4 w-4" />
                    <span>Pagalbos užklausos</span>
                    {supportTotal > 0 && (
                      <span className="ml-auto rounded-full bg-orange-500 px-1.5 text-xs font-medium text-white">
                        {supportTotal}
                      </span>
                    )}
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={isActive("/admin/shapes")} className="pl-8">
                  <Link to="/admin/shapes">
                    <Shapes className="h-4 w-4" />
                    <span>SVG figūros</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={isActive("/admin/users")} className="pl-8">
                  <Link to="/admin/users">
                    <Users className="h-4 w-4" />
                    <span>Vartotojai</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={isActive("/admin/features")} className="pl-8">
                  <Link to="/admin/features">
                    <ToggleLeft className="h-4 w-4" />
                    <span>Funkcijos</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={isActive("/admin/logs")} className="pl-8">
                  <Link to="/admin/logs">
                    <Activity className="h-4 w-4" />
                    <span>AI žurnalai</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={isActive("/admin/errors")} className="pl-8">
                  <Link to="/admin/errors">
                    <Bug className="h-4 w-4" />
                    <span>Klaidos</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={isActive("/admin/performance")} className="pl-8">
                  <Link to="/admin/performance">
                    <Gauge className="h-4 w-4" />
                    <span>Našumas</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </>
          )}
          <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={isActive("/billing")}>
              <Link to="/billing">
                <CreditCard className="h-4 w-4" />
                <span>Prenumerata</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={isActive("/settings")}>
              <Link to="/settings">
                <Settings className="h-4 w-4" />
                <span>Nustatymai</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={handleLogout}>
              <LogOut className="h-4 w-4" />
              <span>Atsijungti</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        {/* The footer's legal links, small and last: they are read once, and
            hidden when the sidebar collapses to icons. */}
        <LegalFooter
          inline
          className="px-2 pb-1 text-xs text-muted-foreground group-data-[collapsible=icon]:hidden"
        />
      </SidebarFooter>

      <SidebarRail />

      {/* Outside the menu, driven by state: it has to survive the click that
          opened it, and it attaches whatever page is behind it. */}
      <SupportDialog open={supportOpen} onOpenChange={setSupportOpen} learnerId={learnerId} />
    </Sidebar>
  );
}
